import { Dispatch, ReactNode, SetStateAction } from "react";
import { Bounce, Id, toast } from "react-toastify";
import { Exception } from 'sass';
import i18next from "i18next";
import {
    isSupplierUnavailableError,
    SUPPLIER_UNAVAILABLE_TOAST_KEY,
    SUPPLIER_UNAVAILABLE_TOAST_MS,
} from "../../../utils/functions/supplierUnavailable";
import { noteSupplierUnavailableForStatus } from "../../../utils/functions/supplierStatus";
import { isSheetSizeUnavailableError } from "../../../utils/functions/oversizeSheet";
import { trackErrorShown } from "../../../utils/functions/analytics";

export function useSuccessToast(title: string, autoClose: number = 5000): Id {
    const id = toast.success(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce
    });

    return id;
}

// errorCode: the HTTP/envelope status when the caller knows it - only the
// code reaches Clarity (event fejl_vist + tag fejlkode, flag
// REACT_APP_CLARITY_EVENTS_ENABLED); the message never does.
export function useErrorToast(title: string, autoClose: number = 5000, errorCode?: number | string | null): Id {
    trackErrorShown(errorCode)
    const id = toast.error(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

export function useLoadingToast(title: string, autoClose: number = 5000): Id {
    const id = toast.loading(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

export function dissmissToast(id: Id) {
    toast.dismiss(id)
}

// Non-hook-named aliases: the use*Toast helpers above are plain functions,
// but their names trip the react-hooks lint rules when called from effects
// or handlers inside components. New call sites should prefer these.
export function showSuccessToast(title: string, autoClose: number = 5000): Id {
    return useSuccessToast(title, autoClose);
}

export function showErrorToast(title: string, autoClose: number = 5000, errorCode?: number | string | null): Id {
    return useErrorToast(title, autoClose, errorCode);
}

export function showLoadingToast(title: string, autoClose: number = 5000): Id {
    return useLoadingToast(title, autoClose);
}

export function showInfoToast(title: string, autoClose: number = 8000): Id {
    const id = toast.info(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

// autoClose = false: the toast stays until the user closes it (the PDF sharp
// gate's "PDF not downloaded" when no dialog can be shown any more).
export function showWarningToast(title: string, autoClose: number | false = 8000): Id {
    const id = toast.warning(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

// Replaces the CONTENT of a loading toast that is still on screen (a loading
// toast never auto-closes; withToasts dismisses it): a text, or an element
// (the PDF sharp gate's text + "Cancel"). Unknown / already dismissed id =
// no-op (react-toastify ignores it).
export function updateLoadingToast(id: Id, content: ReactNode): void {
    toast.update(id, { render: content });
}

interface ContextError { message: String, source: String, statusCode: number }

// Optional extras for withToasts. Omitted = exactly the classic behaviour.
export interface WithToastsOptions<T> {
    // Answers false when the transaction RESOLVED without the success the
    // message claims (the PDF sharp gate stopped before saving and opens its
    // own dialog): no success toast, no error toast.
    showSuccess?: (result: T) => boolean;
    // Hands the caller the loading toast's id so it can update the text
    // while the transaction runs (updateLoadingToast).
    onLoadingToast?: (id: Id) => void;
}

export async function withToasts<T>(
    asyncFunction: () => Promise<T>,
    loadingMessage: string,
    successMessage: string,
    errorMessage: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
    options?: WithToastsOptions<T>
): Promise<T> {
    setIsLoading && setIsLoading(true);
    const toast = useLoadingToast(loadingMessage)
    options?.onLoadingToast?.(toast)

    try {
        const result = await asyncFunction()
        if (!options?.showSuccess || options.showSuccess(result)) {
            useSuccessToast(successMessage)
        }
        return result;
    } catch (error) {
        // Flag REACT_APP_SUPPLIER_STATUS_ENABLED: a 503 supplierUnavailable
        // (or the client-side upload timeout) asks the status banner for an
        // immediate re-check - before the toast, whatever the toast flag
        // says. Flag off = no-op.
        noteSupplierUnavailableForStatus(error)
        // Flag REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED: the API's fail-fast
        // 503 supplierUnavailable (or the client-side upload timeout) gets
        // ONE clear toast instead of the compound "Could not …: <message>".
        // Rethrown as every other error, so the caller's own finally still
        // stops its spinner and rolls back its optimistic state. Flag off =
        // isSupplierUnavailableError is always false = exactly today's toast.
        if (isSupplierUnavailableError(error)) {
            useErrorToast(
                i18next.t(SUPPLIER_UNAVAILABLE_TOAST_KEY),
                SUPPLIER_UNAVAILABLE_TOAST_MS,
                503
            )
            throw error
        }
        // Flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED: the API's 409
        // sheetSizeUnavailable at CommitOrder/PlaceOrder is answered by the
        // oversize-sheet dialog (opened by the caller's catch), not by the
        // compound "Could not place order: …" toast. Rethrown as every other
        // error, so the caller's own finally still stops its spinner. Flag
        // off = isSheetSizeUnavailableError is always false = today's toast.
        if (isSheetSizeUnavailableError(error)) {
            throw error
        }
        const contextError = error as ContextError
        let compoundErrorMessage = errorMessage
        if (contextError && contextError.message) {
            if (errorMessage) {
                compoundErrorMessage += ': ' + contextError.message
            } else {
                compoundErrorMessage = contextError.message as string
            }
        }

        useErrorToast(compoundErrorMessage, 5000, contextError?.statusCode)
        throw error
    } finally {
        dissmissToast(toast)
        setIsLoading && setIsLoading(false)
    }
}